export type FaqItem = {
  question: string;
  answer: string;
};

export type FaqCategory = {
  title: string;
  description: string;
  items: FaqItem[];
};

export const providerFaqs: FaqItem[] = [
  {
    question: 'What is HealthcareLX?',
    answer:
      'HealthcareLX is an AI-assisted healthcare workforce marketplace that connects providers with healthcare organizations through job search, applications, career mobility, and credential-aware hiring workflows.',
  },
  {
    question: 'How does MedCredVault work with HealthcareLX?',
    answer:
      'MedCredVault and HealthcareLX are designed to work together. A provider profile can connect credentials, career preferences, and professional documents to marketplace workflows so providers can apply more efficiently and reduce repetitive onboarding tasks.',
  },
  {
    question: 'Can I control what information employers see?',
    answer:
      'Yes. Providers maintain control over sharing permissions and visibility settings. You can choose which documents, profile sections, or credential categories are shared with organizations.',
  },
  {
    question: 'Is HealthcareLX free for providers?',
    answer:
      'Yes. Providers can generally use HealthcareLX for job search, applications, workforce analytics, and career exploration without paying a provider fee.',
  },
  {
    question: 'What kinds of opportunities can I find?',
    answer:
      'The marketplace may support permanent, part-time, locum tenens, travel, per diem, flexible scheduling, telemedicine, remote, and hybrid opportunities across multiple specialties and care settings.',
  },
  {
    question: 'How does job matching work?',
    answer:
      'AI-assisted matching can consider specialty, licensure, certifications, location preferences, compensation expectations, schedule preferences, employment type, productivity expectations, employer requirements, and market demand trends.',
  },
  {
    question: 'Can MedCredVault help me understand my market value?',
    answer:
      'Yes. The platform is designed to surface compensation benchmarks, productivity comparisons, RVU trends, schedule and workload patterns, regional demand, and other workforce insights when enough profile and market data is available.',
  },
  {
    question: 'Can I disable the HealthcareLX integration?',
    answer:
      'Yes. Providers can modify or disable integration settings from their account dashboard. Disabling the integration may stop profile synchronization, limit credential sharing, or remove visibility from certain recruiting workflows.',
  },
];

export const organizationFaqs: FaqItem[] = [
  {
    question: 'How does HealthcareLX help healthcare organizations recruit providers?',
    answer:
      'HealthcareLX supports healthcare recruiting by helping facilities post opportunities, match with qualified candidates, access credential-aware provider profiles, streamline screening, reduce onboarding friction, and monitor hiring pipeline activity.',
  },
  {
    question: 'How does MedCredVault streamline credentialing for organizations?',
    answer:
      'MedCredVault centralizes provider profiles, organizes licenses and certifications, supports intelligent data population, monitors expiring credentials, and helps organizations reuse provider data across onboarding and credentialing workflows.',
  },
  {
    question: 'What provider information can facilities access?',
    answer:
      'Access depends on provider permissions and organizational workflows. Shared information may include licenses, certifications, DEA registrations, education, training, work history, malpractice coverage, peer references, compensation structures, productivity metrics, and career preferences.',
  },
  {
    question: 'How does the platform support compliance readiness?',
    answer:
      'The platform supports centralized records, verification workflows, audit tracking, automated notifications, expiration monitoring, and recredentialing workflows tied to licenses, certifications, CME requirements, DEA registrations, and malpractice coverage.',
  },
  {
    question: 'What workforce insights are available to organizations?',
    answer:
      'Organizations may use workforce analytics for compensation benchmarking, RVU and productivity comparisons, regional labor market insights, hiring conditions, scheduling trends, retention planning, and recruitment strategy.',
  },
  {
    question: 'How is provider information secured?',
    answer:
      'Security controls may include encryption in transit and at rest, role-based access controls, secure authentication, audit logging, monitoring, and provider-controlled sharing permissions.',
  },
  {
    question: 'Can HealthcareLX reduce onboarding delays?',
    answer:
      'Yes. Credential-linked profiles and reusable provider data can help organizations review qualifications faster, reduce repetitive document collection, and move from recruiting to onboarding with less administrative delay.',
  },
  {
    question: 'How do facilities get started?',
    answer:
      'Organizations typically start by scheduling a consultation or demo, configuring workflow needs, onboarding recruiting or credentialing teams, and connecting provider workflows and job postings.',
  },
];

export const faqCategories: FaqCategory[] = [
  {
    title: 'Marketplace Basics',
    description: 'How MedCredVault and HealthcareLX work together for healthcare hiring.',
    items: [
      {
        question: 'What is HealthcareLX?',
        answer:
          'HealthcareLX is an AI-assisted healthcare workforce marketplace built for healthcare hiring, credentialing, applications, negotiations, onboarding, and career mobility.',
      },
      {
        question: 'How does MedCredVault integrate with HealthcareLX?',
        answer:
          'MedCredVault provides a provider-owned credential and career profile that can connect to HealthcareLX. The integration helps providers share credentials, apply for opportunities, receive recommendations, and reduce repeated paperwork during onboarding.',
      },
      {
        question: 'Who can use the platform?',
        answer:
          'The platform is designed for physicians, nurses, advanced practice providers, physician assistants, allied health professionals, healthcare leaders, hospitals, health systems, medical groups, staffing organizations, and recruiting teams.',
      },
      {
        question: 'Do providers need separate accounts?',
        answer:
          'No. MedCredVault and HealthcareLX are designed to function through an integrated account structure, with profile information able to synchronize across connected workflows when enabled.',
      },
    ],
  },
  {
    title: 'Providers',
    description: 'Credential sharing, job matching, and career insights for individual providers.',
    items: providerFaqs.slice(2, 7),
  },
  {
    title: 'Organizations',
    description: 'Recruiting, onboarding, compliance, and workforce intelligence for facilities.',
    items: organizationFaqs.slice(0, 7),
  },
  {
    title: 'Security And Guidance',
    description: 'How data control, security, and regulatory variation are handled.',
    items: [
      {
        question: 'Who owns and controls provider data?',
        answer:
          'Providers maintain control over their professional information and sharing permissions. Organizations can access information only through provider permissions and approved workflows.',
      },
      {
        question: 'Is provider information secure?',
        answer:
          'The platform uses safeguards such as encrypted storage and transmission, role-based access controls, secure sharing permissions, audit tracking, monitoring, and HIPAA-conscious security practices.',
      },
      {
        question: 'Is individual provider data sold?',
        answer:
          'No. Individual provider data is not sold to third parties. Aggregate or anonymized information may be used for market intelligence and benchmarking where permitted by platform policies.',
      },
      {
        question: 'Does this FAQ replace official credentialing or licensing guidance?',
        answer:
          'No. Healthcare credentialing, licensure, payer enrollment, malpractice, compensation, and employment requirements vary by state, specialty, employer, facility, practice setting, and regulatory authority. Providers and organizations should verify requirements with licensing boards, credentialing organizations, accreditation agencies, and appropriate advisors.',
      },
    ],
  },
];
